import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../components/ui/toast/use-toast';
import { 
  Loader, Star, ThumbsUp, ThumbsDown, HelpCircle, 
  Smile, Frown, Meh, Edit, Send, X, 
  CheckCircle, ArrowRight, Heart, Award,
  AlertTriangle
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const SaleExperienceModal = ({ isOpen, onClose, propertyId, propertyTitle, onSuccess }) => {
  const { token } = useAuth();
  const { toast } = useToast();
  const [submitting, setSubmitting] = useState(false);
  
  // Estado para los campos obligatorios
  const [saleExperience, setSaleExperience] = useState({
    satisfaction: null,
    recommendation: null,
    experienceRating: null,
    feedback: ''
  });
  
  // Estado para campos adicionales
  const [additionalFeedback, setAdditionalFeedback] = useState({
    dificultadUso: null,
    sugerenciaMejora: '',
    calificacionUi: null,
    calificacionSoporte: null,
    calificacionPrecio: null
  });
  
  // Estado para mostrar/ocultar sección avanzada
  const [showAdvanced, setShowAdvanced] = useState(false);
  
  // Estado para manejar la animación por pasos
  const [currentStep, setCurrentStep] = useState(0);
  const [showNextButton, setShowNextButton] = useState(false);
  
  // Estado para el aviso de eliminación
  const [showDeletionWarning, setShowDeletionWarning] = useState(false);
  
  // Opciones para dificultad de uso
  const dificultadOptions = [
    { value: 'facil', label: 'Fácil', icon: <Smile className="w-5 h-5" /> },
    { value: 'moderado', label: 'Moderado', icon: <Meh className="w-5 h-5" /> },
    { value: 'dificil', label: 'Difícil', icon: <Frown className="w-5 h-5" /> }
  ];
  
  // Definición de los pasos
  const steps = [
    { 
      id: 'satisfaction', 
      title: '¿Cómo te sentiste vendiendo con nosotros?',
      fieldKey: 'satisfaction',
      icon: <Heart className="w-5 h-5" />
    },
    { 
      id: 'recommendation', 
      title: '¿Recomendarías nuestra plataforma?',
      fieldKey: 'recommendation',
      icon: <ThumbsUp className="w-5 h-5" />
    },
    { 
      id: 'experienceRating', 
      title: 'Califica tu experiencia general',
      fieldKey: 'experienceRating',
      icon: <Star className="w-5 h-5" />
    },
    { 
      id: 'feedback', 
      title: 'Comparte tus comentarios',
      fieldKey: 'feedback',
      icon: <Edit className="w-5 h-5" />
    }
  ];
  
  // Manejar el siguiente paso
  const handleNextStep = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
      setShowNextButton(false);
    } else {
      // Mostrar advertencia de eliminación antes de enviar
      setShowDeletionWarning(true);
    }
  };
  
  // Manejar el paso anterior
  const handlePrevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      setShowNextButton(true);
    }
  };
  
  // Revisar si el paso actual está completo
  useEffect(() => {
    const currentStepField = steps[currentStep]?.fieldKey;
    
    if (currentStepField) {
      const fieldValue = saleExperience[currentStepField];
      
      if (currentStepField === 'feedback') {
        // Feedback es opcional, siempre permitir avanzar
        setShowNextButton(true);
      } else {
        // Otros campos son obligatorios
        setShowNextButton(fieldValue !== null && fieldValue !== undefined);
      }
    }
  }, [saleExperience, currentStep]);

  // Verificar si todos los campos obligatorios están completos
  const isFormValid = () => {
    return (
      saleExperience.satisfaction !== null &&
      saleExperience.recommendation !== null &&
      saleExperience.experienceRating !== null
    );
  };

  // Enviar experiencia al backend
  const handleSubmit = async () => {
    if (!isFormValid()) {
      toast({
        title: 'Datos incompletos',
        description: 'Por favor, completa todos los campos obligatorios.',
        variant: 'destructive'
      });
      return;
    }
    
    try {
      setSubmitting(true);
      
      const response = await fetch('https://backend-production-1786.up.railway.app/api/ventas/experiencia', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          propertyId,
          ...saleExperience,
          ...additionalFeedback
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error al guardar la experiencia');
      }

      const data = await response.json();
      
      // Mostrar mensaje de éxito
      toast({
        title: 'Experiencia guardada',
        description: 'Gracias por compartir tu experiencia con nosotros.',
        variant: 'default'
      });
      
      // Ejecutar callback de éxito si existe
      if (onSuccess) {
        onSuccess(data);
      }
      
      // Cerrar modal
      onClose();
    } catch (error) {
      console.error('Error al enviar experiencia:', error);
      toast({
        title: 'Error',
        description: error.message || 'No se pudo guardar tu experiencia.',
        variant: 'destructive'
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Si el modal no está abierto, no renderizar nada
  if (!isOpen) return null;
  
  // Obtener el paso actual
  const currentStepData = steps[currentStep];

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
        >
          <motion.div 
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 400, damping: 30 }}
            className="bg-white rounded-xl w-full max-w-2xl shadow-2xl overflow-hidden"
          >
            {/* Modal de advertencia de eliminación */}
            <AnimatePresence>
              {showDeletionWarning && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="absolute inset-0 bg-black bg-opacity-75 flex items-center justify-center z-10 p-6"
                >
                  <motion.div
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.8, opacity: 0 }}
                    className="bg-white rounded-xl p-6 max-w-lg w-full"
                  >
                    <div className="mb-4 flex items-center justify-center">
                      <div className="flex items-center justify-center w-16 h-16 rounded-full bg-amber-100 text-amber-600">
                        <AlertTriangle className="w-8 h-8" />
                      </div>
                    </div>
                    <h3 className="text-xl font-bold text-center mb-2">Atención</h3>
                    <p className="text-gray-700 text-center mb-4">
                      Al enviar esta experiencia, la publicación <span className="font-semibold">"{propertyTitle || 'Propiedad'}"</span> será marcada como vendida y eliminada de tus listados activos.
                    </p>
                    <p className="text-center mb-6 text-gray-700">
                      Muchas gracias por confiar en Inkayros.com
                    </p>
                    <div className="flex justify-center space-x-4">
                      <button
                        onClick={() => setShowDeletionWarning(false)}
                        className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100"
                      >
                        Cancelar
                      </button>
                      <button
                        onClick={handleSubmit}
                        className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                        disabled={submitting}
                      >
                        {submitting ? (
                          <>
                            <Loader className="w-5 h-5 animate-spin" />
                            <span>Enviando...</span>
                          </>
                        ) : (
                          <>
                            <Send className="w-5 h-5" />
                            <span>Enviar Experiencia</span>
                          </>
                        )}
                      </button>
                    </div>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>
            
            {/* Encabezado */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white p-6">
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold">Tu Experiencia de Venta</h2>
                <button 
                  onClick={onClose}
                  className="p-1 rounded-full hover:bg-white/20 transition-colors"
                  aria-label="Cerrar"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              <p className="mt-2 text-blue-100">
                Tu opinión nos ayuda a mejorar nuestra plataforma. 
                ¡Gracias por compartir tu experiencia!
              </p>
            </div>
            
            {/* Indicador de pasos */}
            <div className="bg-gray-50 px-6 py-3">
              <div className="flex justify-center gap-1">
                {steps.map((step, index) => (
                  <div key={step.id} className="flex items-center">
                    <div 
                      className={`flex items-center justify-center w-8 h-8 rounded-full 
                        ${currentStep === index 
                          ? 'bg-blue-500 text-white' 
                          : index < currentStep 
                            ? 'bg-green-500 text-white' 
                            : 'bg-gray-200 text-gray-500'
                        } transition-all duration-300 cursor-pointer`}
                      onClick={() => index <= currentStep && setCurrentStep(index)}
                    >
                      {index < currentStep ? (
                        <CheckCircle className="w-5 h-5" />
                      ) : (
                        step.icon
                      )}
                    </div>
                    {index < steps.length - 1 && (
                      <div className={`w-8 h-1 ${index < currentStep ? 'bg-green-500' : 'bg-gray-200'}`}></div>
                    )}
                  </div>
                ))}
              </div>
            </div>
            
            {/* Contenido principal por pasos */}
            <div className="p-6 min-h-[300px] flex flex-col justify-between">
              <AnimatePresence mode="wait">
                <motion.div 
                  key={currentStep}
                  initial={{ x: 50, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  exit={{ x: -50, opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  className="space-y-6"
                >
                  <h3 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
                    {currentStepData.icon}
                    {currentStepData.title}
                  </h3>
                  
                  {/* Componente dinámico según el paso actual */}
                  {currentStepData.id === 'satisfaction' && (
                    <div className="space-y-4">
                      <div className="flex justify-center gap-3">
                        {[1, 2, 3, 4, 5].map((val) => (
                          <motion.button
                            key={val}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => setSaleExperience(prev => ({
                              ...prev, 
                              satisfaction: val
                            }))}
                            className={`flex flex-col items-center justify-center w-20 h-20 rounded-lg transition-all ${
                              saleExperience.satisfaction === val 
                                ? 'bg-blue-500 text-white shadow-lg' 
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                          >
                            <span className="text-2xl font-bold">{val}</span>
                            <span className="text-xs mt-1">
                              {val === 1 ? 'Malo' : val === 2 ? 'Regular' : val === 3 ? 'Bien' : val === 4 ? 'Muy bien' : 'Excelente'}
                            </span>
                          </motion.button>
                        ))}
                      </div>
                      <p className="text-center text-gray-500 text-sm mt-4">
                        Selecciona un valor del 1 al 5, donde 1 es "Malo" y 5 es "Excelente"
                      </p>
                    </div>
                  )}
                  
                  {currentStepData.id === 'recommendation' && (
                    <div className="space-y-4">
                      <div className="flex justify-center gap-6">
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => setSaleExperience(prev => ({
                            ...prev, 
                            recommendation: true
                          }))}
                          className={`flex flex-col items-center gap-3 px-8 py-6 rounded-lg transition-all ${
                            saleExperience.recommendation === true 
                              ? 'bg-green-500 text-white shadow-lg' 
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          <ThumbsUp className="w-10 h-10" />
                          <span className="font-medium">Sí, lo recomendaría</span>
                        </motion.button>
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => setSaleExperience(prev => ({
                            ...prev, 
                            recommendation: false
                          }))}
                          className={`flex flex-col items-center gap-3 px-8 py-6 rounded-lg transition-all ${
                            saleExperience.recommendation === false
                              ? 'bg-red-500 text-white shadow-lg' 
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          <ThumbsDown className="w-10 h-10" />
                          <span className="font-medium">No lo recomendaría</span>
                        </motion.button>
                      </div>
                      <p className="text-center text-gray-500 text-sm mt-4">
                        ¿Recomendarías nuestra plataforma a amigos o familiares?
                      </p>
                    </div>
                  )}
                  
                  {currentStepData.id === 'experienceRating' && (
                    <div className="space-y-4">
                      <div className="flex justify-center items-center gap-3">
                        {[1, 2, 3, 4, 5].map((val) => (
                          <motion.button
                            key={val}
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            onClick={() => setSaleExperience(prev => ({
                              ...prev, 
                              experienceRating: val
                            }))}
                            className="group flex items-center justify-center w-14 h-14 relative"
                          >
                            <Star 
                              className={`w-10 h-10 absolute ${
                                val <= saleExperience.experienceRating
                                  ? 'text-yellow-400 fill-yellow-400' 
                                  : 'text-gray-300 group-hover:text-gray-400'
                              }`} 
                            />
                          </motion.button>
                        ))}
                      </div>
                      <p className="text-center text-gray-700 font-medium mt-4">
                        {saleExperience.experienceRating 
                          ? `Has calificado con ${saleExperience.experienceRating} ${saleExperience.experienceRating === 1 ? 'estrella' : 'estrellas'}`
                          : 'Selecciona una calificación de 1 a 5 estrellas'}
                      </p>
                    </div>
                  )}
                  
                  {currentStepData.id === 'feedback' && (
                    <div className="space-y-4">
                      <textarea
                        value={saleExperience.feedback}
                        onChange={(e) => setSaleExperience(prev => ({
                          ...prev, 
                          feedback: e.target.value
                        }))}
                        className="w-full p-4 border rounded-lg min-h-[150px] text-gray-800 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all"
                        placeholder="Comparte cualquier comentario adicional que pueda ayudarnos a mejorar..."
                      />
                      <p className="text-gray-500 text-sm">
                        Este campo es opcional pero muy valioso para nosotros.
                      </p>
                      
                      {/* Opción para ver campos avanzados */}
                      <div className="mt-4">
                        <button
                          onClick={() => setShowAdvanced(!showAdvanced)}
                          className="flex items-center gap-2 text-blue-600 hover:text-blue-800 transition-colors"
                        >
                          {showAdvanced ? 
                            <span className="flex items-center"><Edit className="w-4 h-4 mr-1" /> Ocultar opciones avanzadas</span> : 
                            <span className="flex items-center"><Edit className="w-4 h-4 mr-1" /> Mostrar opciones avanzadas</span>
                          }
                        </button>
                        
                        <AnimatePresence>
                          {showAdvanced && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.3 }}
                              className="overflow-hidden mt-4"
                            >
                              <div className="space-y-6 bg-gray-50 p-4 rounded-lg">
                                {/* Dificultad de uso */}
                                <div>
                                  <label className="block text-md font-medium mb-2 text-gray-800">
                                    ¿Qué te pareció la dificultad de uso de la plataforma?
                                  </label>
                                  <div className="flex gap-3">
                                    {dificultadOptions.map(option => (
                                      <motion.button
                                        key={option.value}
                                        whileHover={{ scale: 1.05 }}
                                        whileTap={{ scale: 0.95 }}
                                        onClick={() => setAdditionalFeedback(prev => ({
                                          ...prev,
                                          dificultadUso: option.value
                                        }))}
                                        className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                                          additionalFeedback.dificultadUso === option.value
                                            ? 'bg-blue-100 text-blue-800 border border-blue-300 shadow-sm'
                                            : 'bg-white text-gray-700 hover:bg-gray-100 border border-transparent'
                                        }`}
                                      >
                                        {option.icon}
                                        <span>{option.label}</span>
                                      </motion.button>
                                    ))}
                                  </div>
                                </div>
                                
                                {/* Calificaciones específicas */}
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                  {/* UI */}
                                  <div>
                                    <label className="block text-sm font-medium mb-2 text-gray-800">
                                      Interfaz de usuario
                                    </label>
                                    <div className="flex">
                                      {[1, 2, 3, 4, 5].map((val) => (
                                        <button
                                          key={`ui-${val}`}
                                          onClick={() => setAdditionalFeedback(prev => ({
                                            ...prev,
                                            calificacionUi: val
                                          }))}
                                          className={`w-8 h-8 flex items-center justify-center ${
                                            val <= additionalFeedback.calificacionUi
                                              ? 'bg-blue-500 text-white'
                                              : 'bg-white text-gray-700 hover:bg-gray-100'
                                          } ${val === 1 ? 'rounded-l-lg' : ''} ${val === 5 ? 'rounded-r-lg' : ''} border border-gray-200`}
                                        >
                                          {val}
                                        </button>
                                      ))}
                                    </div>
                                  </div>
                                  
                                  {/* Soporte */}
                                  <div>
                                    <label className="block text-sm font-medium mb-2 text-gray-800">
                                      Soporte al cliente
                                    </label>
                                    <div className="flex">
                                      {[1, 2, 3, 4, 5].map((val) => (
                                        <button
                                          key={`soporte-${val}`}
                                          onClick={() => setAdditionalFeedback(prev => ({
                                            ...prev,
                                            calificacionSoporte: val
                                          }))}
                                          className={`w-8 h-8 flex items-center justify-center ${
                                            val <= additionalFeedback.calificacionSoporte
                                              ? 'bg-blue-500 text-white'
                                              : 'bg-white text-gray-700 hover:bg-gray-100'
                                          } ${val === 1 ? 'rounded-l-lg' : ''} ${val === 5 ? 'rounded-r-lg' : ''} border border-gray-200`}
                                        >
                                          {val}
                                        </button>
                                      ))}
                                    </div>
                                  </div>
                                  
                                  {/* Precio/Valor */}
                                  <div>
                                    <label className="block text-sm font-medium mb-2 text-gray-800">
                                      Relación precio/valor
                                    </label>
                                    <div className="flex">
                                      {[1, 2, 3, 4, 5].map((val) => (
                                        <button
                                          key={`precio-${val}`}
                                          onClick={() => setAdditionalFeedback(prev => ({
                                            ...prev,
                                            calificacionPrecio: val
                                          }))}
                                          className={`w-8 h-8 flex items-center justify-center ${
                                            val <= additionalFeedback.calificacionPrecio
                                              ? 'bg-blue-500 text-white'
                                              : 'bg-white text-gray-700 hover:bg-gray-100'
                                          } ${val === 1 ? 'rounded-l-lg' : ''} ${val === 5 ? 'rounded-r-lg' : ''} border border-gray-200`}
                                        >
                                          {val}
                                        </button>
                                      ))}
                                    </div>
                                  </div>
                                </div>
                                
                                {/* Sugerencias de mejora */}
                                <div>
                                  <label className="block text-md font-medium mb-2 text-gray-800">
                                    ¿Tienes alguna sugerencia específica para mejorar nuestra plataforma?
                                  </label>
                                  <textarea
                                    value={additionalFeedback.sugerenciaMejora}
                                    onChange={(e) => setAdditionalFeedback(prev => ({
                                      ...prev,
                                      sugerenciaMejora: e.target.value
                                    }))}
                                    className="w-full p-3 border rounded-lg min-h-[100px] text-gray-800 bg-white"
                                    placeholder="Comparte tus ideas para mejorar nuestra plataforma..."
                                  />
                                </div>
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    </div>
                  )}
                </motion.div>
              </AnimatePresence>
            </div>
            
            {/* Botones de navegación */}
            <div className="border-t p-6 bg-gray-50 flex justify-between">
              <button
                onClick={handlePrevStep}
                className={`px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors ${
                  currentStep === 0 ? 'opacity-50 cursor-not-allowed' : ''
                }`}
                disabled={currentStep === 0 || submitting}
              >
                Anterior
              </button>
              
              <motion.button
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                onClick={handleNextStep}
                className={`px-6 py-2 bg-blue-600 text-white rounded-lg transition-colors flex items-center gap-2 ${
                  showNextButton ? 'hover:bg-blue-700' : 'opacity-50 cursor-not-allowed'
                }`}
                disabled={!showNextButton || submitting}
              >
                {submitting ? (
                  <>
                    <Loader className="w-5 h-5 animate-spin" />
                    <span>Enviando...</span>
                  </>
                ) : currentStep === steps.length - 1 ? (
                  <>
                    <Send className="w-5 h-5" />
                    <span>Finalizar</span>
                  </>
                ) : (
                  <>
                    <span>Siguiente</span>
                    <ArrowRight className="w-5 h-5" />
                  </>
                )}
              </motion.button>
            </div>
            
            {/* Indicador de progreso */}
            <div className="bg-gray-100 h-2">
              <div 
                className="h-full bg-blue-500 transition-all duration-300"
                style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
              ></div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default SaleExperienceModal;